import { SaveButton, Show } from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import {
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  notification,
  Row,
  Select,
  Space,
  Statistic,
} from 'antd';
import BatchSection from 'components/product-make/BatchSection';
import ExpenseSection from 'components/product-make/ExpenseSection';
import RecipeTable from 'components/product-make/RecipeTable';
import WastageSection from 'components/product-make/WastageSection';
import { ManufactureStatus } from 'enums';
import { useDebounce } from 'hooks';
import React, { useEffect, useState } from 'react';
import {
  createManufacture,
  findRecipeWithStatus,
  getProductWithRecipesList,
} from 'services/product-manufacture';
import { getTransactionToken } from 'services/transaction';
import { getWarehousesDropdown, IWarehouseDropDown } from 'services/warehouse';
import { calculateTotalManufacturingCost, scrollToFieldWithOffset } from 'utilities/shared';

interface Product {
  variantId: string;
  variantName: string;
}

type ProductListResponse = Product[];

interface RecipeDetailsResponse {
  madeUpVariantName?: string;
  madeUpVariantSku?: string;
  purchasePrice?: number;
  sellingPrice?: number;
  usedProductRecipes?: UsedProductRecipe[];
  madeUpVariantId?: string;
}

interface UsedProductRecipe {
  inventoryQuantity?: number;
  availability?: string;
  note?: string;
  usedProductCost?: number;
  usedProductQuantity?: number;
  usedVariantId?: string;
  usedVariantName?: string;
  usedVariantSku?: string;
  usedProductUnitPrice?: number;
}

const { Option } = Select;

export const MakeProduct: React.FC = () => {
  const { list } = useNavigation();
  const [form] = Form.useForm();
  const [warehouses, setWarehouses] = useState<IWarehouseDropDown[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [productList, setProductList] = useState<Product[] | null>(null);
  const [recipeDetails, setRecipeDetails] = useState<RecipeDetailsResponse | null>(null);
  const [isQuantityDisabled, setIsQuantityDisabled] = useState<boolean>(true);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
  // Batch Section State
  const [batchData, setBatchData] = useState<any[]>([]);
  const [editingBatch, setEditingBatch] = useState<any>(null);
  const [batchDrawerVisible, setBatchDrawerVisible] = useState<boolean>(false);
  // Wastage Section State
  const [wastageData, setWastageData] = useState<any[]>([]);
  const [editingWastage, setEditingWastage] = useState<any>(null);
  const [wastageDrawerVisible, setWastageDrawerVisible] = useState<boolean>(false);
  // Expense Section State
  const [expenseData, setExpenseData] = useState<any[]>([]);
  const [editingExpense, setEditingExpense] = useState<any>(null);
  const [expenseDrawerVisible, setExpenseDrawerVisible] = useState<boolean>(false);
  const debouncedQuantity = useDebounce(quantity, 500);

  useEffect(() => {
    (async () => {
      try {
        setIsFetching(true);
        const [warehousesResponse] = await Promise.all([getWarehousesDropdown()]);
        const warehousesData = warehousesResponse.map((warehouse) => {
          return {
            id: warehouse.id,
            name: warehouse.name,
          };
        });
        setWarehouses(warehousesData);
      } catch (error: any) {
        notification.error({
          message: 'Faield',
          description: error.message,
        });
      }
      setIsFetching(false);
    })();
  }, []);

  // Effect to trigger API call when debounced value changes
  useEffect(() => {
    const fetchRecipeDetails = async () => {
      if (
        !debouncedQuantity ||
        isNaN(Number(debouncedQuantity)) ||
        Number(debouncedQuantity) <= 0
      ) {
        return;
      }

      if (!selectedProductId || !warehouseId) {
        return;
      }

      form.setFieldsValue({ productStatus: null });

      try {
        setIsFetching(true);
        const recipeInput = {
          data: {
            variantId: selectedProductId,
            variantQuantity: Number(debouncedQuantity),
            warehouseId: warehouseId,
          },
        };

        const recipeDetailsResponse = await findRecipeWithStatus(recipeInput);
        setRecipeDetails(recipeDetailsResponse);
      } catch (error: any) {
        notification.error({
          message: 'Failed to fetch recipe details',
          description: error.message,
        });
      } finally {
        setIsFetching(false);
      }
    };

    if (selectedProductId && warehouseId) {
      fetchRecipeDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuantity, selectedProductId, warehouseId]);

  const onSelectWarehouse = async (warehouseId: string | undefined) => {
    if (!warehouseId) return;

    setWarehouseId(warehouseId);
    setProductList(null);
    setSelectedProductId(null);
    setIsQuantityDisabled(true);
    setRecipeDetails(null);
    setQuantity(null);

    form.setFieldsValue({ product: null, quantity: null });

    try {
      setIsFetching(true);
      const products: ProductListResponse = await getProductWithRecipesList();
      setProductList(products);
    } catch (error: any) {
      notification.error({ message: 'Failed to fetch products', description: error.message });
    } finally {
      setIsFetching(false);
    }
  };

  const onSelectProduct = async (productId: string) => {
    if (!productId || !warehouseId) return;

    setSelectedProductId(productId);
    setIsQuantityDisabled(false);
    setRecipeDetails(null);
    setQuantity(null);

    form.setFieldsValue({ quantity: null });
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuantity(Number(e.target.value));
  };

  const onStatusCheck = (value: string) => {
    const newStatus = value as ManufactureStatus;

    if (recipeDetails && recipeDetails.usedProductRecipes) {
      const unavailableMaterials = recipeDetails.usedProductRecipes.filter(
        (recipe) => recipe.availability && recipe.availability === 'Not Available',
      );
      const hasUnavailableMaterials = unavailableMaterials.length > 0;
      const shouldBlockStatusChange =
        hasUnavailableMaterials && newStatus !== ManufactureStatus['Not Started'];

      if (shouldBlockStatusChange) {
        setIsSaveDisabled(true);
        notification.warning({
          message: 'Material Unavailable',
          description:
            'Some materials are not available. Please reduce the quantity or check the availability of materials.',
        });
        return;
      }

      setIsSaveDisabled(false);
    }
  };

  const getOptions = (items: IWarehouseDropDown[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  // Handlers for Batch Section
  const handleBatchEdit = (record: any) => {
    setEditingBatch(record);
    setBatchDrawerVisible(true);
  };

  const handleBatchDelete = (key: string) => {
    setBatchData(batchData.filter((item) => item.key !== key));
  };

  const handleBatchSubmit = (newBatch: any) => {
    if (editingBatch) {
      setBatchData(
        batchData.map((item) => (item.key === editingBatch.key ? { ...item, ...newBatch } : item)),
      );
    } else {
      setBatchData([...batchData, { ...newBatch, key: Date.now().toString() }]);
    }
    setEditingBatch(null);
    setBatchDrawerVisible(false);
  };

  // Handlers for Wastage Section
  const handleWastageEdit = (record: any) => {
    setEditingWastage(record);
    setWastageDrawerVisible(true);
  };

  const handleWastageDelete = (key: string) => {
    setWastageData(wastageData.filter((item) => item.key !== key));
  };

  const handleWastageSubmit = (newWastage: any) => {
    if (editingWastage) {
      setWastageData(
        wastageData.map((item) =>
          item.key === editingWastage.key ? { ...item, ...newWastage } : item,
        ),
      );
    } else {
      setWastageData([...wastageData, { ...newWastage, key: Date.now().toString() }]);
    }
    setEditingWastage(null);
    setWastageDrawerVisible(false);
  };

  // Handlers for Expense Section
  const handleExpenseEdit = (record: any) => {
    setEditingExpense(record);
    setExpenseDrawerVisible(true);
  };

  const handleExpenseDelete = (key: string) => {
    setExpenseData(expenseData.filter((item) => item.key !== key));
  };

  const handleExpenseSubmit = (newExpense: any) => {
    if (editingExpense) {
      setExpenseData(
        expenseData.map((item) =>
          item.key === editingExpense.key ? { ...item, ...newExpense } : item,
        ),
      );
    } else {
      setExpenseData([...expenseData, { ...newExpense, key: Date.now().toString() }]);
    }
    setEditingExpense(null);
    setExpenseDrawerVisible(false);
  };

  // General Form Validation Function
  const getFormValidationErrors = (formValues: any) => {
    const errors: any[] = [];

    // Warehouse validation
    if (!formValues.warehouse) {
      errors.push({
        name: 'warehouse',
        errors: ['Please select a warehouse.'],
      });
    }

    // Product validation
    if (!formValues.product) {
      errors.push({
        name: 'product',
        errors: ['Please select a product.'],
      });
    }

    // Planned Quantity validation
    if (
      !formValues.quantity || // Check if quantity is provided
      isNaN(Number(formValues.quantity)) || // Check if it's a valid number
      formValues.quantity <= 0 || // Ensure it's positive
      !Number.isInteger(Number(formValues.quantity)) // Ensure it's an integer
    ) {
      errors.push({
        name: 'quantity',
        errors: ['Please enter a valid planned quantity (positive integer).'],
      });
    }

    // Deadline validation
    if (!formValues.deadline) {
      errors.push({
        name: 'deadline',
        errors: ['Please select a deadline.'],
      });
    } else {
      const today = new Date();
      const selectedDate = new Date(formValues.deadline);
      if (selectedDate.getTime() < today.setHours(0, 0, 0, 0)) {
        errors.push({
          name: 'deadline',
          errors: ['Deadline cannot be in the past.'],
        });
      }
    }

    if (!formValues.productStatus) {
      errors.push({
        name: 'productStatus',
        errors: ['Please select a product status.'],
      });
    }

    return errors.length ? errors : null;
  };

  const handleSave = async () => {
    setIsFetching(true);
    try {
      // Get form data
      const formData = await form.getFieldsValue();
      const validationError = getFormValidationErrors(formData);

      if (validationError !== null) {
        form.setFields(validationError);
        scrollToFieldWithOffset(validationError[0].name as string, 100);
        return;
      }

      const transactionToken = await getTransactionToken();

      const manufactureProducts =
        recipeDetails?.usedProductRecipes?.map((item) => ({
          materialSku: item.usedVariantSku,
          quantity: Number(item.usedProductQuantity),
          unitPrice: Number(item.usedProductUnitPrice),
        })) || [];

      const manufactureExpense = expenseData.map((item) => ({
        name: item.expenseCategory,
        notes: item.note || '',
        amount: Number(item.amount),
      }));

      const manufactureWastage = wastageData.map((item) => ({
        material: item.material,
        notes: item.notes || '',
        quantity: Number(item.quantity),
        wastageCost: Number(item.cost),
      }));

      let productionDeadlineDate = new Date();
      if (formData.deadline && !isNaN(new Date(formData.deadline).getTime())) {
        productionDeadlineDate = new Date(formData.deadline);
      }
      const formattedDeadlineDate = productionDeadlineDate.toISOString();

      let expirationDate: Date | undefined;
      if (batchData.length > 0 && batchData[0].expiration) {
        const [day, month, year] = batchData[0].expiration.split('-');
        const date = new Date(`${year}-${month}-${day}`);
        if (!isNaN(date.getTime())) {
          expirationDate = date;
        }
      }
      const formattedExpirationDate = expirationDate?.toISOString() ?? null;

      const batchInfo =
        batchData.length > 0
          ? {
              batchNo: batchData[0]?.batch || 'N/A',
              expirationDate: formattedExpirationDate,
              notes: batchData[0]?.notes || '',
            }
          : null;

      const requestData = {
        transactionToken,
        warehouse: formData.warehouse,
        variantId: formData.product,
        quantity: Number(quantity),
        productionDeadlineDate: formattedDeadlineDate,
        manufactureStatus: formData.productStatus,
        manufactureProducts,
        manufactureExpense,
        manufactureWastage,
        batchInfo,
      };

      await createManufacture(requestData);

      notification.success({
        message: 'Success',
        description: 'Created successfully!',
      });

      form.resetFields();
      setBatchData([]);
      setWastageData([]);
      setExpenseData([]);
      setQuantity(null);
      setRecipeDetails(null);
      setSelectedProductId(null);
      list('product-make');
    } catch (error: any) {
      console.error('Submission Failed:', error);
      notification.error({
        message: 'Error',
        description: error?.message || 'Failed to create manufacturing process. Please try again.',
      });
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Show headerProps={{ extra: null }}>
      {/* General Information Form */}
      <Form form={form} layout="vertical" style={{ marginBottom: 32 }}>
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={12}>
            <Card title="Make Product">
              <Form.Item
                label="Warehouse"
                name="warehouse"
                rules={[{ required: true, message: 'Please select a warehouse' }]}
              >
                <Select
                  showSearch
                  placeholder="Select a warehouse"
                  loading={isFetching}
                  disabled={isFetching || !warehouses.length}
                  onSelect={(value: string) => onSelectWarehouse(value?.toString())}
                  filterOption={(input, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(warehouses)}
                </Select>
              </Form.Item>
              <Form.Item
                label="Product"
                name="product"
                rules={[{ required: true, message: 'Please select a product' }]}
              >
                <Select
                  placeholder="Select Product"
                  loading={isFetching}
                  disabled={isFetching || !productList?.length}
                  onSelect={(value: string) => onSelectProduct(value)}
                >
                  {productList?.map((product: any) => (
                    <Option key={product.variantId} value={product.variantId}>
                      {product.variantName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Planned Quantity"
                name="quantity"
                rules={[
                  { required: true, message: 'Please enter the planned quantity' },
                  {
                    type: 'number',
                    min: 1,
                    message: 'Quantity must be a positive integer',
                    transform: (value) => (value ? Number(value) : 0), // Convert value to number
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder="Enter Quantity"
                  disabled={isQuantityDisabled}
                  // onChange={(e) => onQuantityChange(e.target.value)}
                  onChange={handleQuantityChange} // Uses debounced state update
                />
              </Form.Item>
              {/* New Product Status Dropdown */}
              <Form.Item
                label="Product Status"
                name="productStatus"
                rules={[{ required: true, message: 'Please select a product status' }]}
              >
                <Select
                  placeholder="Select Product Status"
                  disabled={!quantity}
                  onSelect={(value) => onStatusCheck(value)}
                >
                  {Object.values(ManufactureStatus).map((status) => (
                    <Option key={status} value={status}>
                      {status}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Product Deadline"
                name="deadline"
                rules={[
                  { required: true, message: 'Please select a deadline' },
                  {
                    validator: (_, value) =>
                      value && value.isBefore(new Date(), 'day')
                        ? Promise.reject(new Error('Deadline cannot be in the past'))
                        : Promise.resolve(),
                  },
                ]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>

      {/* Recipe Table */}
      <RecipeTable
        recipeData={recipeDetails}
        expenseData={expenseData}
        wastageData={wastageData}
        loading={isFetching}
      />
      <Divider />

      {/* Batch Section */}
      <BatchSection
        data={batchData}
        visible={batchDrawerVisible}
        editingRecord={editingBatch}
        onEdit={handleBatchEdit}
        onDelete={handleBatchDelete}
        onSubmit={handleBatchSubmit}
        onClose={() => setBatchDrawerVisible(false)}
        disabled={isSaveDisabled}
      />

      <Divider />

      {/* Wastage Section */}
      <WastageSection
        data={wastageData}
        visible={wastageDrawerVisible}
        editingRecord={editingWastage}
        recipeDetails={recipeDetails} // Pass recipeDetails here
        onEdit={handleWastageEdit}
        onDelete={handleWastageDelete}
        onSubmit={handleWastageSubmit}
        onClose={() => setWastageDrawerVisible(false)}
        disabled={isSaveDisabled}
      />

      <Divider />

      {/* Expense Section */}
      <ExpenseSection
        data={expenseData}
        visible={expenseDrawerVisible}
        editingRecord={editingExpense}
        onEdit={handleExpenseEdit}
        onDelete={handleExpenseDelete}
        onSubmit={handleExpenseSubmit}
        onClose={() => setExpenseDrawerVisible(false)}
        disabled={isSaveDisabled}
      />
      <Divider />

      {/* Total Manufacturing Cost Calculation */}
      <Card style={{ marginBottom: 16 }} hidden>
        <Row justify="end">
          <Col>
            <Statistic
              title="Total Manufacturing Cost"
              value={`${calculateTotalManufacturingCost(
                recipeDetails?.usedProductRecipes,
                wastageData,
                expenseData,
              ).toFixed(2)}`}
              valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Save Button */}
      <Space style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
        <SaveButton
          type="primary"
          style={{ marginTop: 16 }}
          onClick={handleSave}
          disabled={isSaveDisabled}
          loading={isFetching}
        >
          Save
        </SaveButton>
      </Space>
    </Show>
  );
};

export default MakeProduct;
